import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import {
  Building2, TrendingUp, ChevronRight,
  Heart, Zap, Palette, AlertTriangle, ArrowUpDown, ExternalLink,
  Layers, Activity, Shield, Cpu, Briefcase
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { usePrefetchOrFetch } from "../common/DataPrefetch";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const OC = { care: "#10b981", utility: "#3b82f6", creative: "#a855f7", extraction: "#ef4444" };

const SECTOR_ICONS = {
  "ITeS": Cpu, "Financial Services": Shield, "Healthcare": Heart,
  "Media & Entertainment": Palette, "Education & Research": Building2,
  "Enterprise SaaS": Briefcase, "Retail & Consumer": Layers,
  "Legal & GRC": Shield, "Defense & Security": AlertTriangle,
  "Agriculture & Climate": Activity, "Manufacturing": Cpu,
  "Social Impact": Heart, "Transportation & Logistics": TrendingUp,
  "Real Estate & Infrastructure": Building2,
};

const SECTOR_COLORS = [
  "#f59e0b", "#10b981", "#3b82f6", "#a855f7", "#ef4444",
  "#06b6d4", "#ec4899", "#84cc16", "#f97316", "#6366f1",
  "#14b8a6", "#e11d48", "#8b5cf6", "#eab308",
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0c1222] border border-white/10 rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="font-bold text-white mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name || p.dataKey} style={{ color: p.color || "#94a3b8" }}>{p.name}: {typeof p.value === "number" ? p.value.toLocaleString() : p.value}</p>
      ))}
    </div>
  );
};

/* ── Skeleton shimmer blocks ── */
const Shimmer = ({ className, style }) => (
  <div className={`animate-pulse bg-white/[0.04] rounded-lg ${className}`} style={style} />
);

const HeroSkeleton = () => (
  <div className="relative rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, #0a0f1a 0%, #0f1520 50%, #0a0f1a 100%)" }}>
    <div className="relative px-8 py-10 text-center space-y-3">
      <Shimmer className="h-3 w-40 mx-auto" />
      <Shimmer className="h-9 w-64 mx-auto" />
      <Shimmer className="h-4 w-96 mx-auto" />
    </div>
  </div>
);

const ChartsSkeleton = () => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
    {[1, 2].map(i => (
      <div key={i} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
        <Shimmer className="h-3 w-32 mb-4" />
        <div className="h-80 flex items-end gap-2 px-4">
          {[75, 55, 45, 40, 35, 30, 25, 20, 18, 15, 12, 10, 8, 6].map((h, j) => (
            <Shimmer key={j} className="flex-1" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>
    ))}
  </div>
);

const GridSkeleton = () => (
  <div>
    <Shimmer className="h-4 w-48 mb-4" />
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3, 4, 5, 6].map(i => (
        <div key={i} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 space-y-3">
          <div className="flex items-center gap-2.5">
            <Shimmer className="w-9 h-9 rounded-lg" />
            <div className="space-y-1.5 flex-1"><Shimmer className="h-3 w-20" /><Shimmer className="h-2 w-14" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Shimmer className="h-2 w-10" /><Shimmer className="h-5 w-8" /></div>
            <div className="space-y-1"><Shimmer className="h-2 w-10" /><Shimmer className="h-5 w-10" /></div>
          </div>
          <Shimmer className="h-1.5 w-full" />
        </div>
      ))}
    </div>
  </div>
);

/* ── Sector Card ── */
const SectorCard = ({ sector, index, onSelect }) => {
  const Icon = SECTOR_ICONS[sector.sector] || Briefcase;
  const color = SECTOR_COLORS[index % SECTOR_COLORS.length];
  const orientations = sector.orientations || {};
  const total = Object.values(orientations).reduce((a, b) => a + b, 0) || 1;

  return (
    <div onClick={() => onSelect(sector.sector)}
      className="group bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 hover:border-white/[0.15] transition-all cursor-pointer hover:bg-white/[0.03]"
      data-testid={`sector-card-${sector.sector.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${color}18`, border: `1px solid ${color}30` }}>
            <Icon className="w-4.5 h-4.5" style={{ color }} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white group-hover:text-white/90">{sector.sector}</h3>
            <p className="text-[10px] text-slate-500">{sector.company_count.toLocaleString()} companies</p>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-slate-400 transition-colors" />
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <p className="text-[9px] text-slate-500 uppercase tracking-wider">Avg CII</p>
          <p className="text-lg font-black" style={{ color: sector.avg_cii >= 30 ? "#10b981" : sector.avg_cii >= 15 ? "#f59e0b" : "#ef4444" }}>{sector.avg_cii}</p>
        </div>
        <div>
          <p className="text-[9px] text-slate-500 uppercase tracking-wider">Products</p>
          <p className="text-lg font-black text-slate-200">{sector.total_products.toLocaleString()}</p>
        </div>
      </div>
      <div className="w-full h-1.5 rounded-full overflow-hidden bg-white/[0.04] flex">
        {Object.entries(orientations).filter(([, v]) => v > 0).map(([k, v]) => (
          <div key={k} style={{ width: `${(v / total) * 100}%`, background: OC[k] }} />
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-1">
        {(sector.top_companies || []).slice(0, 3).map((c, i) => (
          <span key={i} className="text-[9px] px-1.5 py-0.5 bg-white/[0.04] border border-white/[0.06] rounded text-slate-400">{c.company_name}</span>
        ))}
      </div>
    </div>
  );
};

/* ── Main Component ── */
export const CompanySolutions = ({ onViewDetail }) => {
  // Sectors come from the bootstrap prefetch store (`companyIndustryMap`) — no per-mount fetch
  const [industryMap] = usePrefetchOrFetch(
    "companyIndustryMap",
    `${API}/companies/industry-map`,
    {
      validate: (d) => d && Array.isArray(d.sectors) && d.sectors.length > 0,
      delay: 500,
    }
  );
  const sectors = useMemo(() => industryMap?.sectors || null, [industryMap]);

  const [selectedSector, setSelectedSector] = useState(null);
  const [sectorCompanies, setSectorCompanies] = useState([]);
  const [sectorLoading, setSectorLoading] = useState(false);
  const [sortBy, setSortBy] = useState("cii");

  const totalCompanies = useMemo(() => sectors ? sectors.reduce((a, s) => a + s.company_count, 0) : 0, [sectors]);

  // Race-guard so rapid sector switching doesn't show the wrong company list
  const reqIdRef = useRef(0);

  const loadSectorCompanies = async (sector) => {
    setSelectedSector(sector);
    setSectorLoading(true);
    setSectorCompanies([]); // clear stale rows immediately so user sees skeleton, not old data
    const myReq = ++reqIdRef.current;
    try {
      const res = await axios.get(`${API}/companies/solutions`, {
        params: { sector, sort_by: sortBy, limit: 50 },
        timeout: 30000,
      });
      if (reqIdRef.current === myReq) setSectorCompanies(res.data.companies || []);
    } catch {
      if (reqIdRef.current === myReq) setSectorCompanies([]);
    }
    if (reqIdRef.current === myReq) setSectorLoading(false);
  };

  useEffect(() => {
    if (selectedSector) loadSectorCompanies(selectedSector);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy]);

  const loaded = sectors !== null;

  const chartData = (sectors || []).map(s => ({
    name: s.sector.length > 18 ? s.sector.slice(0, 16) + ".." : s.sector,
    companies: s.company_count, cii: s.avg_cii,
  }));

  return (
    <div className="space-y-8 pb-8" data-testid="company-solutions">
      {/* ─── Hero ─── */}
      {!loaded ? <HeroSkeleton /> : (
        <div className="relative rounded-2xl overflow-hidden animate-fade-in" style={{ background: "linear-gradient(135deg, #0a0f1a 0%, #0f1520 50%, #0a0f1a 100%)" }}>
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 70% 30%, rgba(168,85,247,0.06), transparent 60%)" }} />
          <div className="relative px-8 py-10 text-center">
            <p className="text-[10px] font-bold text-violet-400 uppercase tracking-[0.3em] mb-2">AI Solutions by Industry</p>
            <h1 className="text-3xl sm:text-4xl font-black text-white mb-3">Enterprise AI Landscape</h1>
            <p className="text-sm text-slate-400 max-w-2xl mx-auto leading-relaxed">
              <span className="text-violet-400 font-bold">{sectors.length}</span> enterprise sectors mapped across{" "}
              <span className="text-amber-400 font-bold">{totalCompanies.toLocaleString()}</span> companies.
              <span className="text-slate-500 ml-1">Explore how AI solutions distribute across industries and where ethical innovation concentrates.</span>
            </p>
          </div>
        </div>
      )}

      {selectedSector ? (
        /* ─── Drill-down view ─── */
        <div className="space-y-6 animate-fade-in">
          <div className="flex items-center gap-4">
            <button onClick={() => setSelectedSector(null)} className="text-xs text-violet-400 hover:text-violet-300 font-medium" data-testid="back-to-sectors">All Sectors</button>
            <ChevronRight className="w-3 h-3 text-slate-600" />
            <span className="text-sm font-bold text-white">{selectedSector}</span>
          </div>

          {(() => {
            const s = (sectors || []).find(x => x.sector === selectedSector);
            if (!s) return null;
            return (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: "Companies", val: s.company_count.toLocaleString(), color: "text-white" },
                  { label: "Avg CII", val: s.avg_cii, color: s.avg_cii >= 30 ? "text-emerald-400" : "text-amber-400" },
                  { label: "Products", val: s.total_products.toLocaleString(), color: "text-slate-200" },
                  { label: "Care-Oriented", val: Math.round((s.orientations.care / s.company_count) * 100) + "%", color: "text-emerald-400" },
                ].map((item, i) => (
                  <div key={i} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
                    <p className="text-[9px] text-slate-500 uppercase tracking-wider">{item.label}</p>
                    <p className={`text-2xl font-black ${item.color}`}>{item.val}</p>
                  </div>
                ))}
              </div>
            );
          })()}

          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-white/[0.06] flex items-center justify-between">
              <h3 className="text-sm font-bold text-white">Companies in {selectedSector}</h3>
              <div className="flex items-center gap-2">
                <ArrowUpDown className="w-3 h-3 text-slate-500" />
                <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                  className="text-[10px] px-2 py-1 bg-white/[0.04] border border-white/[0.08] rounded-lg text-slate-300 focus:outline-none" data-testid="sector-sort">
                  <option value="cii">Sort: CII</option>
                  <option value="products">Sort: Products</option>
                  <option value="name">Sort: Name</option>
                </select>
              </div>
            </div>
            {sectorLoading ? (
              <div className="divide-y divide-white/[0.03]">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="flex items-center gap-3 px-5 py-3">
                    <Shimmer className="w-6 h-4" />
                    <div className="flex-1 space-y-1.5"><Shimmer className="h-3.5 w-28" /><Shimmer className="h-2.5 w-40" /></div>
                    <Shimmer className="w-10 h-5" /><Shimmer className="w-8 h-5" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="divide-y divide-white/[0.03]">
                {sectorCompanies.map((c, i) => (
                  <div key={c.domain} className="group" data-testid={`company-row-${c.domain}`}>
                    <div className="flex items-center gap-3 px-5 py-3 hover:bg-white/[0.02] transition-colors">
                      <span className="text-xs font-bold text-slate-600 w-6 text-right">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-white truncate">{c.company_name}</span>
                          <span className="text-[9px] px-2 py-0.5 rounded-full border" style={{
                            color: OC[c.value_orientation], borderColor: `${OC[c.value_orientation]}33`, background: `${OC[c.value_orientation]}11`,
                          }}>{c.value_orientation}</span>
                        </div>
                        <p className="text-[10px] text-slate-500">{c.domain} &middot; {c.country}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-black" style={{ color: c.cii_score >= 30 ? "#10b981" : c.cii_score >= 15 ? "#f59e0b" : "#ef4444" }}>{c.cii_score}</p>
                        <p className="text-[9px] text-slate-500">CII</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-slate-300">{c.product_count}</p>
                        <p className="text-[9px] text-slate-500">products</p>
                      </div>
                      {onViewDetail && (
                        <button onClick={() => onViewDetail(c.domain)} className="opacity-0 group-hover:opacity-100 text-amber-400 hover:text-amber-300 transition-opacity" data-testid={`view-detail-${i}`}>
                          <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    {/* Product pills */}
                    {c.product_details && c.product_details.length > 0 && (
                      <div className="px-5 pb-3 pl-14 flex flex-wrap gap-1.5">
                        {(c.product_details || c.products || []).slice(0, 6).map((p) => {
                          const pName = typeof p === "string" ? p : p.name;
                          const pVo = typeof p === "string" ? c.value_orientation : (p.value_orientation || c.value_orientation);
                          return (
                            <span key={pName || `prod-${pVo}`} className="text-[9px] px-2 py-0.5 rounded-full border" style={{
                              color: OC[pVo] || "#94a3b8", borderColor: `${OC[pVo] || "#94a3b8"}22`, background: `${OC[pVo] || "#94a3b8"}08`,
                            }}>{pName}</span>
                          );
                        })}
                        {(c.product_details || []).length > 6 && (
                          <span className="text-[9px] text-slate-500">+{(c.product_details || []).length - 6} more</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {sectorCompanies.length === 0 && <p className="text-center text-sm text-slate-500 py-8">No companies in this sector</p>}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* ─── Overview ─── */
        <>
          {!loaded ? <ChartsSkeleton /> : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in">
              <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5" data-testid="sector-bar-chart">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Companies by Sector</h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} layout="vertical" margin={{ left: 100 }}>
                      <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} />
                      <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} width={100} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="companies" name="Companies" fill="#a855f7" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5" data-testid="sector-cii-chart">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">CII by Sector</h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={[...chartData].sort((a, b) => b.cii - a.cii)} layout="vertical" margin={{ left: 100 }}>
                      <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} domain={[0, 100]} />
                      <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} width={100} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="cii" name="Avg CII" fill="#10b981" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {!loaded ? <GridSkeleton /> : (
            <div className="animate-fade-in">
              <h2 className="text-sm font-bold text-slate-300 uppercase tracking-wider mb-4">Browse by Enterprise Sector</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sectors.map((s, i) => <SectorCard key={s.sector} sector={s} index={i} onSelect={loadSectorCompanies} />)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CompanySolutions;
